import { Button, Col, Flex, Form, Input, InputNumber, Row } from "antd";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { formItemSharedStyles } from "~constants/sharedStyles.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { useGetSeasonStage } from "~pages/SeasonStages/hooks/useGetSeasonStage.ts";
import { useSeasonStageService } from "~pages/SeasonStages/hooks/useSeasonStageService.ts";
import Page from "../../../Common/Page";
import LoaderContainer from "../../../Common/Page/LoaderContainer";
import PermissionCheck from "../../../Common/PermissionCheck";

const SeasonStageEditPage = () => {
  const { data: seasonStage, isLoading } = useGetSeasonStage();
  const { customerId, sport, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const { updateSeasonStage, isUpdatePending: isUpdatingSeasonStage } = useSeasonStageService();
  const pageTitle = seasonStage != null ? seasonStage.nameLocal : formatMessage({ id: "loading" });

  useEffect(() => {
    if (!seasonStage) {
      return;
    }

    form.setFieldsValue({
      nameLocal: seasonStage.nameLocal,
      nameLatin: seasonStage.nameLatin ?? null,
      abbreviationLocal: seasonStage.abbreviationLocal ?? null,
      abbreviationLatin: seasonStage.abbreviationLatin ?? null,
      externalId: seasonStage.externalId ?? null,
      stageOrder: seasonStage.stageOrder ?? null,
    });
  }, [seasonStage, form]);

  const onUpdateButtonClick = async () => {
    if (!seasonStage?.stageCode) {
      return;
    }

    const formValues = (await form.validateFields()) ?? {};

    await updateSeasonStage(seasonStage.stageCode, {
      nameLocal: formValues.nameLocal,
      nameLatin: formValues.nameLatin || null,
      abbreviationLocal: formValues.abbreviationLocal || null,
      abbreviationLatin: formValues.abbreviationLatin || null,
      externalId: formValues.externalId || null,
      stageOrder: formValues.stageOrder ?? null,
    });
  };

  return (
    <Page title={pageTitle}>
      <LoaderContainer loading={isLoading}>
        <Row>
          <Col span={12}>
            <Flex vertical gap="middle">
              <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <Form.Item
                  name="nameLocal"
                  label={formatMessage({ id: "name.local" })}
                  tooltip={formatMessage({ id: "form.help.local.name.item" }, { item: formatMessage({ id: "stage" }) })}
                  style={formItemSharedStyles}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="abbreviationLocal"
                  label={formatMessage({ id: "name.local.abbreviation" })}
                  tooltip={formatMessage({ id: "form.help.local.abbreviation" })}
                  style={formItemSharedStyles}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="nameLatin"
                  label={formatMessage({ id: "name.latin" })}
                  tooltip={formatMessage({ id: "form.help.latin.name.item" }, { item: formatMessage({ id: "stage" }) })}
                  style={formItemSharedStyles}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="abbreviationLatin"
                  label={formatMessage({ id: "name.latin.abbreviation" })}
                  tooltip={formatMessage({ id: "form.help.latin.abbreviation" })}
                  style={formItemSharedStyles}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="externalId"
                  label={formatMessage({ id: "externalId" })}
                  tooltip={formatMessage({ id: "externalId" })}
                  style={formItemSharedStyles}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="stageOrder"
                  label={formatMessage({ id: "stage.order" })}
                  tooltip={formatMessage({ id: "form.help.stage.order" })}
                  style={formItemSharedStyles}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Form>
              <Flex justify="flex-end" gap="large">
                <PermissionCheck permissionKeys={[PermissionNames.STAGE]} permissionOptions={[PermissionOperations.UPDATE]}>
                  <Button
                    type="primary"
                    onClick={() => void onUpdateButtonClick()}
                    disabled={isUpdatingSeasonStage || !seasonStage?.stageCode}
                  >
                    {formatMessage({ id: "stage.update" })}
                  </Button>
                </PermissionCheck>
                <Link to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/stages`}>
                  <Button type="primary">{formatMessage({ id: "close" })}</Button>
                </Link>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </LoaderContainer>
    </Page>
  );
};

export default SeasonStageEditPage;
